import type {
  Attribute,
  BooleanAttribute,
  FloatAttribute,
  IntegerAttribute,
  StringAttribute,
} from 'domain/experiment/attribute/attribute-model';
import { getAttribute } from 'domain/experiment/attribute/attribute-model-utils';
import { KnownAttributes } from 'domain/experiment/attribute/known-attributes';

export type RunLineageAttributes = {
  requestedParent?: StringAttribute;
  effectiveParent?: StringAttribute;
  parentNotFound?: BooleanAttribute;
  forkDepth?: IntegerAttribute;
  forkStep?: FloatAttribute;
  inheritedDataReady?: BooleanAttribute;
};

export type RunLineageWarningKind =
  | 'effective-parent-differs-from-requested-parent'
  | 'effective-parent-missing-requested-parent'
  | 'fork-depth-missing-effective-parent'
  | 'inherited-data-not-ready'
  | 'requested-parent-missing-effective-parent';

export type RunLineageWarning = {
  kind: RunLineageWarningKind;
  message: string;
};

const RUN_LINEAGE_WARNING_MESSAGES: Record<RunLineageWarningKind, string> = {
  'effective-parent-differs-from-requested-parent':
    'Effective parent differs from requested parent.',
  'effective-parent-missing-requested-parent':
    'Effective parent is set, but no requested parent is recorded.',
  'fork-depth-missing-effective-parent': 'Fork depth is set, but no effective parent is recorded.',
  'inherited-data-not-ready': 'Inherited data is still being prepared.',
  'requested-parent-missing-effective-parent':
    'Requested parent is set, but no effective parent is recorded.',
};

export function getRunLineageAttributes(attributes: Attribute[]): RunLineageAttributes {
  return {
    requestedParent: getAttribute(attributes, KnownAttributes.ForkingRequestedParent, 'string'),
    effectiveParent: getAttribute(attributes, KnownAttributes.ForkingEffectiveParent, 'string'),
    parentNotFound: getAttribute(attributes, KnownAttributes.ForkingParentNotFound, 'bool'),
    forkDepth: getAttribute(attributes, KnownAttributes.ForkingDepth, 'int'),
    forkStep: getAttribute(attributes, KnownAttributes.ForkingStep, 'float'),
    inheritedDataReady: getAttribute(attributes, KnownAttributes.ForkingInheritedDataReady, 'bool'),
  };
}

export function hasRunLineageAttributes(lineage: RunLineageAttributes): boolean {
  return Object.values(lineage).some(Boolean);
}

export function deriveRunLineageWarnings(lineage: RunLineageAttributes): RunLineageWarning[] {
  const warnings: RunLineageWarning[] = [];
  const requestedParent = lineage.requestedParent?.value;
  const effectiveParent = lineage.effectiveParent?.value;
  const forkDepth = lineage.forkDepth?.value;

  if (lineage.inheritedDataReady?.value === false) {
    warnings.push(createRunLineageWarning('inherited-data-not-ready'));
  }

  if (requestedParent && effectiveParent && requestedParent !== effectiveParent) {
    warnings.push(createRunLineageWarning('effective-parent-differs-from-requested-parent'));
  }

  if (requestedParent && !effectiveParent) {
    warnings.push(createRunLineageWarning('requested-parent-missing-effective-parent'));
  }

  if (!requestedParent && effectiveParent) {
    warnings.push(createRunLineageWarning('effective-parent-missing-requested-parent'));
  }

  if (forkDepth !== undefined && forkDepth > 0 && !effectiveParent) {
    warnings.push(createRunLineageWarning('fork-depth-missing-effective-parent'));
  }

  return warnings;
}

function createRunLineageWarning(kind: RunLineageWarningKind): RunLineageWarning {
  return {
    kind,
    message: RUN_LINEAGE_WARNING_MESSAGES[kind],
  };
}
